import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import BottomNav from '../components/ui/BottomNav'
import TopBar from '../components/ui/TopBar'
import Panel from '../components/ui/Panel'
import Avatar from '../components/ui/Avatar'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import QrCode from '../components/ui/QrCode'
import { useToast } from '../components/ui/Toast'
import texts from '../texts'
import { createGame, getClub, getClubs, getGames, getMe, inviteUrl } from '../lib/api'
import type { Club, ClubDetails, Me } from '../lib/api'

const REFRESH_MS = 5000

function defaultGameName(): string {
  const date = new Date().toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
  return texts.newGame.defaultName(date)
}

function NewGame() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [me, setMe] = useState<Me | null>(null)
  const [clubs, setClubs] = useState<Club[]>([])
  const [clubDetails, setClubDetails] = useState<ClubDetails | null>(null)
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null)
  const [selected, setSelected] = useState<number[]>([])
  const [name, setName] = useState(defaultGameName())
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([getMe(), getClubs(), getGames()])
      .then(([meData, clubsData, gamesData]) => {
        setMe(meData)
        setClubs(clubsData)
        if (clubsData.length > 0) {
          const newestGame = gamesData.games[0]
          const newestClubId = newestGame?.clubId
          const defaultClub = clubsData.find((c) => c.id === newestClubId) ?? clubsData[0]
          setSelectedClubId(defaultClub.id)
        }
      })
      .catch(() => {
        // Klub- og spillerlisten forbliver tom, hvis den ikke kan hentes
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!selectedClubId || !me) return
    let cancelled = false

    const load = () =>
      getClub(selectedClubId).then((details) => {
        if (cancelled) return
        setClubDetails(details)
        setSelected((prev) => {
          const memberIds = details.members.map((m) => m.userId)
          const kept = prev.filter((id) => id === me.id || memberIds.includes(id))
          return kept.includes(me.id) ? kept : [...kept, me.id]
        })
      })

    load()
    const interval = window.setInterval(load, REFRESH_MS)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [selectedClubId, me])

  const toggle = (id: number) => {
    if (me && id === me.id) return
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= 6) {
        showToast(texts.newGame.maxPlayers)
        return prev
      }
      return [...prev, id]
    })
  }

  const canStart = selected.length >= 4 && selected.length <= 6

  const handleStart = async () => {
    if (!canStart || !me) return
    setError('')
    setStarting(true)
    try {
      const response = await createGame({
        name: name.trim() || defaultGameName(),
        playerIds: selected,
        clubId: selectedClubId ?? undefined
      })
      navigate(`/game/${response.gameId}`)
    } catch (err: any) {
      setError(err.response?.data?.message || texts.newGame.genericStartError)
    } finally {
      setStarting(false)
    }
  }

  if (loading) {
    return (
      <Screen>
        <TopBar title={texts.newGame.title} back="/" />
        <p className="text-text-2">…</p>
        <BottomNav current="game" />
      </Screen>
    )
  }

  if (clubs.length === 0) {
    return (
      <Screen>
        <TopBar title={texts.newGame.title} back="/" />
        <Panel>
          <b>{texts.newGame.noClubTitle}</b>
          <p className="text-[0.85em] text-text-2">{texts.newGame.noClubHint}</p>
          <Link to="/klubber" className="text-gold">
            {texts.newGame.noClubLink}
          </Link>
        </Panel>
        <BottomNav current="game" />
      </Screen>
    )
  }

  const members = clubDetails?.members ?? []

  return (
    <Screen>
      <TopBar title={texts.newGame.title} back="/" />

      <Input value={name} onChange={(e) => setName(e.target.value)} aria-label={texts.newGame.title} />

      <div className="grid gap-1.5">
        {clubs.map((c) => {
          const isSelected = (selectedClubId ?? clubs[0].id) === c.id
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedClubId(c.id)}
              className={`flex min-h-[44px] w-full items-center gap-2.5 rounded-xl border bg-text-2/10 px-3 py-2.5 text-left text-inherit ${
                isSelected ? 'border-gold' : 'border-text-2/20 opacity-60'
              }`}
            >
              <div className="min-w-0 flex-1">
                <b className="block">{c.name}</b>
                <span className="text-[0.8em] text-text-2">{texts.clubs.members(c.memberCount)}</span>
              </div>
              {isSelected && <span>✓</span>}
            </button>
          )
        })}
      </div>

      <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
        {texts.newGame.playersHeading(selected.length)}
      </span>
      <div className="grid gap-1.5">
        {members.map((member, i) => {
          const isSelf = member.userId === me?.id
          const isSelected = selected.includes(member.userId)
          return (
            <button
              key={member.userId}
              type="button"
              onClick={() => toggle(member.userId)}
              disabled={isSelf}
              className={`flex min-h-[44px] w-full items-center gap-2.5 rounded-xl border bg-text-2/10 px-3 py-2.5 text-left text-inherit ${
                isSelected ? 'border-gold' : 'border-text-2/20 opacity-60'
              } ${isSelf ? 'cursor-default' : ''}`}
            >
              <Avatar name={member.name} index={i} />
              <div className="min-w-0 flex-1">
                <b className="block">{member.name}</b>
                <span className="text-[0.8em] text-text-2">
                  {isSelf ? texts.newGame.you : member.isGuest ? texts.club.guestChip : ''}
                </span>
              </div>
              <span>{isSelected ? '✓' : '+'}</span>
            </button>
          )
        })}
      </div>

      {selectedClubId && (
        <Panel>
          <div className="flex gap-3">
            <QrCode value={inviteUrl(clubDetails?.inviteCode ?? '')} size={112} />
            <div className="grid gap-1">
              <b>{texts.newGame.inviteTitle}</b>
              <span className="text-[0.85em] text-text-2">{texts.newGame.inviteHint}</span>
              <span className="break-all font-mono text-[0.85em]">{inviteUrl(clubDetails?.inviteCode ?? '')}</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(inviteUrl(clubDetails?.inviteCode ?? ''))
                showToast(texts.toasts.linkCopied)
              } catch {
                // Kopiering kan mislykkes uden clipboard-adgang
              }
            }}
          >
            {texts.newGame.copyLink}
          </Button>
        </Panel>
      )}

      {error && <p className="text-red">{error}</p>}
      <Button full onClick={handleStart} disabled={!canStart || starting}>
        {texts.newGame.start}
      </Button>
      {!canStart && <p className="text-center text-[0.85em] text-text-2">{texts.newGame.pickCountHint}</p>}

      <BottomNav current="game" />
    </Screen>
  )
}

export default NewGame
